import { Box, Tooltip, Typography } from "@mui/material";
import type { FC } from "react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  type TooltipProps,
} from "recharts";

const DEFAULT_COLORS = [
  "#05204c",
  "#00b4d8",
  "#065bf9",
  "#29bd29",
  "#ee923d",
  "#cb3215",
  "#9747ff",
  "#68686f",
  "#1c2e9d",
  "#18cd4e",
];

export interface PieDatum {
  name: string;
  value: number;
}

interface KpiPieChartProps {
  title: string;
  data: PieDatum[];
  colors?: string[];
  emptyLabel?: string;
}

function sumValues(data: PieDatum[]) {
  return data.reduce((s, d) => s + d.value, 0);
}

function TooltipBody({
  active,
  payload,
  total,
}: TooltipProps<number, string> & { total: number }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const name = String(p.name ?? "");
  const value = Number(p.value ?? 0);
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0";
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: 1,
        maxWidth: 220,
      }}
    >
      <Tooltip title={name} placement="top" enterDelay={400}>
        <Typography
          variant="caption"
          display="block"
          sx={{
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name}
        </Typography>
      </Tooltip>
      <Typography variant="caption" color="text.secondary" component="span">
        {value} ({pct}%)
      </Typography>
    </Box>
  );
}

const indexListSx = {
  flex: 1,
  minWidth: 0,
  maxHeight: 300,
  overflow: "auto",
  pr: 0.5,
  borderRadius: 1,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "action.hover",
  py: 0.5,
} as const;

const indexTooltipProps = {
  placement: "top-start" as const,
  enterDelay: 300,
  slotProps: {
    popper: {
      popperOptions: {
        strategy: "fixed" as const,
        modifiers: [
          { name: "offset", options: { offset: [0, 6] } },
          { name: "preventOverflow", options: { padding: 8 } },
        ],
      },
    },
  },
};

export const KpiPieChart: FC<KpiPieChartProps> = ({
  title,
  data,
  colors = DEFAULT_COLORS,
  emptyLabel = "אין נתונים",
}) => {
  const total = sumValues(data);
  const nonZero = data.filter((d) => d.value > 0);
  const chartData = [...nonZero].sort((a, b) => b.value - a.value);
  const hasData = chartData.length > 0;

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        minHeight: hasData ? 340 : "auto",
      }}
    >
      <Tooltip title={title} placement="top" enterDelay={400}>
        <Box sx={{ width: "100%", mb: 1, px: 0.5 }}>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "text.primary",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
        </Box>
      </Tooltip>
      {!hasData ? (
        <Box
          sx={{
            py: 1.5,
            px: 1,
            borderRadius: 1,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {emptyLabel}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateRows: "auto auto",
            gap: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: { xs: 280, md: 320 },
              alignSelf: "center",
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={72}
                  outerRadius={118}
                  paddingAngle={1}
                  stroke="none"
                  isAnimationActive={false}
                  label={false}
                >
                  {chartData.map((_, i) => (
                    <Cell key={`cell-${i}`} fill={colors[i % colors.length]} />
                  ))}
                </Pie>
                <RechartsTooltip
                  content={<TooltipBody total={total} />}
                  wrapperStyle={{ outline: "none" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>

          <Box sx={indexListSx}>
            {chartData.map((row, i) => {
              const pct =
                total > 0 ? ((row.value / total) * 100).toFixed(0) : "0";
              const fill = colors[i % colors.length];
              return (
                <Box
                  key={`${row.name}-${i}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 0.35,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      flexShrink: 0,
                      bgcolor: fill,
                    }}
                  />
                  <Tooltip title={row.name} {...indexTooltipProps}>
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "block",
                      }}
                    >
                      <Typography
                        variant="caption"
                        noWrap
                        component="span"
                        display="block"
                      >
                        {row.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
                  >
                    {row.value} ({pct}%)
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};
